/**
 * Find Time form field — split-button design:
 *
 *   ┌─────────────────────────────────────────┬─────┐
 *   │  ✨ Find time this week                 │  ▾  │
 *   └─────────────────────────────────────────┴─────┘
 *     main action (applies `this_week`)        menu
 *
 * Clicking the main button directly fills the current week (today
 * → Sunday, weekdays only) — no menu needed for the common case.
 * The small chevron button opens the options list for "Next
 * weekday", "Next N weekdays", "Next week", "Next 2 weeks".
 *
 * Menu items don't carry a persistent "selected" check mark —
 * picking an option fires the apply and closes the menu; the
 * trigger's label doesn't change either. Matches the Figma.
 *
 * All date math anchors on **today**, never the visible calendar
 * week (see `findTime/walker.ts`). Container navigates the calendar
 * to `firstDateKey` on apply so the user lands on the chosen week.
 */
import { useMemo, useState } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Menu, type MenuItemConfig } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { AiStar } from '@/assets/icons/interview';
import {
  FIND_TIME_MODES,
  formatFindTimeLabel,
  formatSlotsSelected,
  walkFindTime,
  type FindTimeValue,
} from '../findTime';

// ─────────────────────────────────────────────────────────────────
// Constants — wording + defaults. Edit here, not in JSX.
// ─────────────────────────────────────────────────────────────────

const MAIN_BUTTON_LABEL = 'Find time this week';
/** The mode the main-button click applies directly. */
const MAIN_BUTTON_MODE: FindTimeValue = 'this_week';

// ─────────────────────────────────────────────────────────────────
// Props.
// ─────────────────────────────────────────────────────────────────

export interface FindTimeApplyArgs {
  value: FindTimeValue;
  targetDateKeys: string[];
  firstDateKey: string | null;
}

export interface FindTimeFieldProps {
  /** Active selection — drives dirty tracking only (no check mark). */
  value: FindTimeValue;
  /** Number of slots currently active across all days. */
  slotsCount: number;
  /** Calendar timezone — drives walker + label formatting. */
  timezone: string;
  /** `true` when the field should be disabled (no interviewer). */
  disabled?: boolean;
  onApply: (_args: FindTimeApplyArgs) => void;
  onClear: () => void;
  onPreview?: () => void;
  minimumNoticeHours?: number;
}

// ─────────────────────────────────────────────────────────────────
// Component.
// ─────────────────────────────────────────────────────────────────

export function FindTimeField({
  value,
  slotsCount,
  timezone,
  disabled = false,
  onApply,
  onClear,
  onPreview,
  minimumNoticeHours,
}: FindTimeFieldProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  // `Date.now()` is impure, so React's compiler forbids calling it
  // during render (including inside `useMemo`). Capture `now` in
  // state at mount and refresh it right when the menu opens, via the
  // Menu's `onOpenChange` — an event callback, not render — so labels
  // reflect a fresh time window each time the user clicks the caret.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const handleMenuOpenChange = (open: boolean) => {
    if (open) setNowMs(Date.now());
    setMenuOpen(open);
  };

  const items = useMemo<MenuItemConfig[]>(
    () =>
      FIND_TIME_MODES.map((mode) => ({
        key: mode.value,
        label: formatFindTimeLabel({ mode, nowMs, timezone }),
        onClick: () => {
          const { dateKeys, firstDateKey } = walkFindTime({
            mode: mode.value,
            nowMs: Date.now(),
            timezone,
          });
          onApply({
            value: mode.value,
            targetDateKeys: dateKeys,
            firstDateKey,
          });
          setMenuOpen(false);
        },
      })),
    [onApply, timezone, nowMs]
  );

  const handleMainClick = () => {
    if (disabled) return;
    const nowMs = Date.now();
    const { dateKeys, firstDateKey } = walkFindTime({
      mode: MAIN_BUTTON_MODE,
      nowMs,
      timezone,
    });
    onApply({
      value: MAIN_BUTTON_MODE,
      targetDateKeys: dateKeys,
      firstDateKey,
    });
  };

  const canPreview = slotsCount > 0;
  const canClear = slotsCount > 0 || value !== null;

  return (
    // Card container — all of the Find Time UI (counter,
    // Preview / Clear, split button, minimum-notice note) sits in
    // one light primary-tint panel with rounded corners. Matches
    // the Figma reference and makes the whole thing read as a
    // single section within the form pane.
    <Box
      w="100%"
      p="3"
      bg="primary-25"
      border="1px solid"
      borderColor="primary-100"
      borderRadius="md"
    >
      {/* ─── Header: counter + Preview + Clear ─── */}
      <Flex align="center" justify="space-between" mb="2">
        <KText fontSize="sm" fontWeight="500" color="secondary-700">
          {formatSlotsSelected(slotsCount)}
        </KText>
        <Flex gap="3">
          {onPreview ? (
            <KText
              as="button"
              fontSize="sm"
              fontWeight="600"
              color={canPreview ? 'primary-500' : 'secondary-400'}
              cursor={canPreview ? 'pointer' : 'not-allowed'}
              onClick={canPreview ? onPreview : undefined}
            >
              Preview
            </KText>
          ) : null}
          <KText
            as="button"
            fontSize="sm"
            fontWeight="600"
            color={canClear ? 'primary-500' : 'secondary-400'}
            cursor={canClear ? 'pointer' : 'not-allowed'}
            onClick={canClear ? onClear : undefined}
          >
            Clear
          </KText>
        </Flex>
      </Flex>

      {/* ─── Split button: [main label | chevron menu] ─── */}
      {/* White button inside the primary-25 card so it pops. */}
      <Flex w="100%" h="40px">
        <Box
          as="button"
          type="button"
          flex="1"
          minW={0}
          display="flex"
          alignItems="center"
          gap="2"
          px="3"
          bg={disabled ? 'secondary-25' : 'white'}
          border="1px solid"
          borderColor={disabled ? 'secondary-100' : 'primary-300'}
          borderRightWidth={0}
          borderLeftRadius="md"
          color={disabled ? 'secondary-400' : 'primary-600'}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.7 : 1}
          _hover={disabled ? undefined : { bg: 'primary-50' }}
          onClick={handleMainClick}
          disabled={disabled}
        >
          <Icon as={AiStar} w="16px" h="16px" />
          <KText fontSize="sm" fontWeight="600">
            {MAIN_BUTTON_LABEL}
          </KText>
        </Box>
        <Menu
          open={menuOpen}
          onOpenChange={handleMenuOpenChange}
          items={items}
          align="end"
          sideOffset={4}
          triggerAsChild
          trigger={
            <Box
              as="button"
              type="button"
              px="2"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg={disabled ? 'secondary-25' : 'white'}
              border="1px solid"
              borderColor={disabled ? 'secondary-100' : 'primary-300'}
              borderRightRadius="md"
              color={disabled ? 'secondary-400' : 'primary-600'}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              opacity={disabled ? 0.7 : 1}
              _hover={disabled ? undefined : { bg: 'primary-50' }}
              aria-label="Open Find Time options"
              disabled={disabled}
            >
              <Icon as={ChevronDown} w="16px" h="16px" />
            </Box>
          }
        />
      </Flex>

      {/* ─── Minimum-notice info note (inline — we're already in a
             card) ─── */}
      {minimumNoticeHours && minimumNoticeHours > 0 ? (
        <KText mt="3" fontSize="sm" color="secondary-700">
          <strong>Note:</strong> Candidates can only book interviews starting{' '}
          <strong>
            {minimumNoticeHours} hour{minimumNoticeHours > 1 ? 's' : ''}
          </strong>{' '}
          from now. Select slots beyond this timeframe to give candidates
          options.
        </KText>
      ) : null}
    </Box>
  );
}
